import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Search, 
  Plus, 
  User, 
  FileText, 
  Calendar, 
  Clock,
  Tag,
  UserCircle,
  Loader2,
  FilePlus
} from "lucide-react";
import { gql, useQuery, useLazyQuery, useMutation, useApolloClient } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import { CalAimForms } from "../../participant/calaim/components/calAIMForms";
import { AddNoteCalAIM } from "../../participant/calaim/modals/AddNoteCalAIM";
import { ParticipantInfoPanel } from "./participantInfoPanel";
import { useUser } from "@/store/useUser";
import { calculateUnits } from "./calculateUnits";
import { FormInput } from "../../participantDocuments/lib/formRender";
import { getFormBehavior } from "../../participantDocuments/behaviors/registry";
import { toast } from "@/components/ui/toast";

// --- Queries ---

const LIST_FORM_TEMPLATES = gql`
  query listFormTemplates {
    listFormTemplates {
      id
      name
      body
    }
  }
`;

const CREATE_PARTICIPANT_DOCUMENT = gql`
  mutation CreateParticipantDocument($updates: GenericJSONDocumentInput!) {
    createParticipantDocument(updates: $updates) {
      id
      contents
      published
      tags            
      metadata
      type
      date
      odrparticipant
      template
    }
  }
`;

const GET_PARTICIPANT_DOCUMENT = gql`
  query GetParticipantDocument($documentID: Int!) {
    getParticipantDocument(documentID: $documentID) {
      id
      odrparticipant
      contents
      template
      tags            
      type
      metadata
    }
  }
`;

const GET_PARTICIPANT_CAREPLAN_GOALS = gql`
  query GetParticipantCarePlanGoals($participantID: String!) {
    getParticipantCarePlanGoals(participantID: $participantID) {
      start_date
      due_date
      problem
      priority
      identified_strengths
      identified_barriers
      smart_goal
      intervention_steps
      goal_status
      outcome_narrative
    }
  }
`;

const SEARCH_PARTICIPANTS = gql`
  query searchParticipants($search: String!) {
    searchParticipants(search: $search) {
      id
      nameHistory {
        firstName
        lastName
      }
      programs
      gender
      mrun
      fin
      champid
    }
  }
`;

const GET_PARTICIPANT_DATA = gql`
  query GetParticipantData($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      name {
        firstName
        lastName
      }
      programDetails {
        programParticipantNumber
        active
      }
      ssn
      cin
      mrun
      fin
      cii
      provider
      healthplan {
        healthplan
      }
      # For CalAimForms
      lastInitialAssessmentDate { id date }
      lastLACAREScreeningDate { id date }
      lastLACAREReferralDate { id date }
      lastProgramCompletionDate: lastProgramCompletion { id date }
      acuity
    }
  }
`;

export const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($odrnumber: String!) {
    calaimNotes(odrnumber: $odrnumber) {
      id
      pos_code
      pos_name
      proceduremodifier
      procedurecode
      proceduremod1
      proceduremod2
      proceduremod3
      proceduretype
      procedurelength
      note_entry_date
      procedurenote
      procedureentery
      entrydate
      entryby
    }
  }
`;

// --- Helpers ---

// Helper function to get procedure code display
const getProcedureCodeDisplay = (note: any) => {
  const modifiers = [note.proceduremodifier, note.proceduremod1, note.proceduremod2, note.proceduremod3]
    .filter(Boolean)
    .join(' ');
  
  return `${note.procedurecode || ''} ${modifiers}`.trim();
};

// --- Main Component ---

export const ParticipantNotes = ({ participant: initialParticipant }: { participant?: string }) => {
  const { participant: paramParticipant } = useParams();
  const navigate = useNavigate();
  
  // Determine the "active" participant ID. 
  // This can come from:
  // 1. URL param (if navigated directly)
  // 2. Internal state (if selected via search on this page)
  const [internalParticipantId, setInternalParticipantId] = useState<string | undefined>(
    initialParticipant || paramParticipant
  );

  const participantId = internalParticipantId;
  
  const [searchInput, setSearchInput] = useState("");
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  
  // Search handling state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  
  const { user } = useUser();

  // --- Data Fetching ---

  // 1. General Search Query (Lazy)
  const [searchParticipants, { loading: searchLoading }] = useLazyQuery(SEARCH_PARTICIPANTS, {
    onCompleted: (data) => {
      if (data?.searchParticipants) {
        const results = data.searchParticipants;
        if (results.length === 0) {
          // No results found
          // Ideally show a toast or inline message
          setSearchResults([]);
          setIsSearchModalOpen(true); // Open modal to show "No results"
        } else if (results.length === 1) {
          // Exactly one result - auto select
          handleSelectParticipant(results[0].id);
        } else {
          // Multiple results - show modal
          setSearchResults(results);
          setIsSearchModalOpen(true);
        }
      }
    }
  });

  // 2. Participant Details Query
  const { data: participantData, loading: loadingParticipant, refetch: refetchParticipant } = useQuery(GET_PARTICIPANT_DATA, {
    variables: { odrnumber: parseInt(participantId || "0") },
    skip: !participantId
  });

  // 3. Notes Query
  const { data: notesData, refetch: refetchNotes } = useQuery(GET_CALAIM_NOTES, {
    variables: { odrnumber: String(participantId || "") },
    skip: !participantId
  });

  // --- Document Creation Logic ---
  const { data: docList } = useQuery(LIST_FORM_TEMPLATES);
  const [createDocument] = useMutation(CREATE_PARTICIPANT_DOCUMENT);
  const client = useApolloClient();

  const [currentSelectedType, setCurrentSelectedType] = useState('');
  const [newDocumentState, setNewDocumentState] = useState<any>({});
  const [newDocumentModal, setNewDocumentModal] = useState(false);
  const [isAltView, setIsAltView] = useState(false);

  // Process templates
  const templateTypesFull = useMemo(() => {
    if (!docList?.listFormTemplates) return [];
    return docList.listFormTemplates.map((template:any) => ({ ...template }));
  }, [docList]);

  const templatesByType = useMemo(() => {
    const types: any = {};
    templateTypesFull.forEach((template: any) => {
      if (template.name === 'Care Plan') {
        const altTemplate = templateTypesFull.find((t: any) => t.name === '_Care Plan');
        if (altTemplate) {
          template.altTemplate = altTemplate.body;
        }
      }
      
      if (!types[template.name]) {
        types[template.name] = template;
      }
    });
    return types;
  }, [templateTypesFull]);

  const templateTypes = useMemo(() => {
    return templateTypesFull.filter((template: any) => !template.name.startsWith('_'));
  }, [templateTypesFull]);

  const getFormTemplate = (formType: string) => {
    const behavior = getFormBehavior(formType);
    const templateData = templatesByType[formType];
    if (behavior.getTemplate) {
      return behavior.getTemplate(templateData, isAltView);
    }
    return templateData?.body || '{}';
  };

  const createNewDocument = async () => {
    if (!currentSelectedType) {
      return toast.error('Please select a document type');
    }
    
    const behavior = getFormBehavior(currentSelectedType);
    let initialFormState: any = {};
    
    if (behavior.getInitialData) {
      initialFormState = behavior.getInitialData({ 
          participant: { ...pData, documents: [] }, 
          user 
      });
    }
    
    // Care Plan specific logic (import goals) - simplified
    if (currentSelectedType === 'Care Plan' && participantId) {
      try {
        const { data: goalsData } = await client.query({
          query: GET_PARTICIPANT_CAREPLAN_GOALS,
          variables: { participantID: participantId.toString() },
        });
        
        if (goalsData?.getParticipantCarePlanGoals?.length > 0) {
          const goals = goalsData.getParticipantCarePlanGoals.map((goal: any) => ({
            start_date: goal.start_date || '',
            due_date: goal.due_date || '',
            problem: goal.problem || '',
            priority: goal.priority || '',
            identified_strengths: goal.identified_strengths || '',
            identified_barriers: goal.identified_barriers || '',
            smart_goal: goal.smart_goal || '',
            intervention_steps: goal.intervention_steps || '',
            goal_status: goal.goal_status || 'in_progress',
            outcome_narrative: goal.outcome_narrative || '',
            import_to_next: false
          }));
          
          initialFormState.goals = goals;
          toast.success(`Imported ${goals.length} incomplete goals from previous care plans`);
        }
      } catch (error) {
        console.error('Error fetching previous goals:', error);
      }
    }
    
    setNewDocumentState(initialFormState);
    setNewDocumentModal(true);
  };

  const handleCreateDocument = async () => {
    try {
      await createDocument({
        variables: {
          updates: {
            contents: JSON.stringify(newDocumentState),
            published: 0,            
            metadata: '{}',
            type: currentSelectedType,            
            odrparticipant: parseInt(participantId || "0"),
            template: getFormTemplate(currentSelectedType)
          }
        }
      });
      toast.success('Document Created');
      setNewDocumentModal(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to create document');
    }
  };

  // --- Handlers ---

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      searchParticipants({ variables: { search: searchInput.trim() } });
    }
  };

  const handleSelectParticipant = (id: string) => {
    setInternalParticipantId(id);
    setIsSearchModalOpen(false);
    setSearchInput(""); // Clear search input? Or keep it? Usually clearing is better after selection.
    setSearchResults([]);
    
    // Note: We are deliberately NOT updating the URL here as requested ("we should not submit a post and change the url here").
    // Ideally, we might want to use navigate(..., { replace: true }) or similar to keep history clean but reflect state, 
    // but we will strictly follow "do it all internally".
  };

  // Process notes for display
  const processedNotes = useMemo(() => {
    if (!notesData?.calaimNotes) return [];
    
    // Sort by date desc
    return [...notesData.calaimNotes].sort((a: any, b: any) => 
      new Date(b.note_entry_date).getTime() - new Date(a.note_entry_date).getTime()
    );
  }, [notesData?.calaimNotes]);

  const pData = participantData?.getParticipantByODRNumber;
  const programs: Array<{
    programParticipantNumber: string | null;
    active: boolean | null;
  } | null> = pData?.programDetails || [];
  const programNumber = (programs.find((program) => program?.active) || programs[0])?.programParticipantNumber;
  const canAddNote = !loadingParticipant && !!programNumber;

  // Update internal state if URL param changes (e.g. browser back button)
  useEffect(() => {
    if (paramParticipant) {
        setInternalParticipantId(paramParticipant);
    }
  }, [paramParticipant]);


  return (
    <div className="space-y-6 p-6">
      {/* Search Section */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Participant Billing</h2>
        <form onSubmit={handleSearch} className="flex gap-2 w-full max-w-sm">
          <Input
            aria-label="Search participants"
            placeholder="Search by Name, SSN, MRUN..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="bg-white"
          />
          <Button type="submit" aria-label="Search participants" disabled={searchLoading}>
            {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </form>
      </div>

      {/* Multiple Results Modal */}
      <Dialog open={isSearchModalOpen} onOpenChange={setIsSearchModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Participant</DialogTitle>
          </DialogHeader>
          
          {searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No participants found matching "{searchInput}"
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-4 font-medium text-sm text-gray-500 border-b pb-2">
                <div className="col-span-2">Name</div>
                <div>MRUN</div>
                <div>FIN</div>
                <div>Programs</div>
              </div>
              {searchResults.map((result) => (
                <div 
                  key={result.id} 
                  className="grid grid-cols-5 gap-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-0 items-center"
                  onClick={() => handleSelectParticipant(result.id)}
                >
                  <div className="col-span-2 font-medium text-gray-900">
                    {result.nameHistory?.[0]?.firstName} {result.nameHistory?.[0]?.lastName}
                    <div className="text-xs text-gray-500">ODR: {result.id}</div>
                  </div>
                  <div className="text-sm text-gray-600">{result.mrun || "—"}</div>
                  <div className="text-sm text-gray-600">{result.fin || "—"}</div>
                  <div className="text-xs text-gray-500">
                    {result.programs?.join(", ") || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {!participantId ? (
        <div className="flex flex-col items-center justify-center h-[400px] text-gray-500 border-2 border-dashed rounded-lg bg-gray-50">
          <User className="h-16 w-16 mb-4 text-gray-300" />
          <p className="text-lg font-medium">No participant selected</p>
          <p className="text-sm">Search for a participant above to view billing details</p>
        </div>
      ) : (
        <>
          {/* Top Panel: Participant Data & ECM Forms */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Participant Info Card */}
            <div className="lg:col-span-2">
              <ParticipantInfoPanel 
                participantData={pData} 
                odrNumber={participantId} 
                refetch={refetchParticipant} 
              />
            </div>

            {/* ECM Forms Card */}
            <div className="lg:col-span-1 space-y-6">
              {pData && <CalAimForms calaimData={pData} odrNumber={participantId} />}

              {/* Document Creation Section */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium">Create New Document</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <select 
                      value={currentSelectedType} 
                      onChange={(e) => setCurrentSelectedType(e.target.value)}
                      className="flex h-9 w-full max-w-sm items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value=''>Select document type...</option>
                      {templateTypes.map((template: any) => (
                        <option key={template.id} value={template.name}>{template.name}</option>
                      ))}
                    </select>
                    <Button onClick={createNewDocument} size="sm" className="bg-green-600 hover:bg-green-700">
                      <FilePlus className="h-4 w-4 mr-2" />
                      Create
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Notes Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-500" />
                Billing Notes
              </h3>
              <Button onClick={() => setIsAddNoteOpen(true)} disabled={!canAddNote} size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </div>

            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> Date
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">
                         <div className="flex items-center gap-1">
                          <Tag className="h-3 w-3" /> Procedure
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> Length
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                        Units
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">
                        Entry Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">
                        Entry By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {processedNotes.length > 0 ? (
                      processedNotes.map((note: any) => (
                        <tr key={note.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(note.note_entry_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {getProcedureCodeDisplay(note)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {note.procedurelength}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                             {/* Logic for units can be complex, using simple calc here if needed or display from backend if available */}
                             {calculateUnits((parseInt(note.procedurelength || "0")).toString())}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate" title={note.procedurenote}>
                            {note.procedurenote}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {new Date(note.entrydate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {note.entryby}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          No billing notes found for this participant.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Add Note Modal */}
          {participantId && pData && canAddNote && programNumber && (
            <AddNoteCalAIM
              isOpen={isAddNoteOpen}
              onClose={() => {
                setIsAddNoteOpen(false);
                refetchNotes();
              }}
              user={user}
              calaimData={{
                fin: pData.fin
              }}
              odrNumber={participantId}
              participant={programNumber}
              refetch={refetchNotes}
            />
          )}
        </>
      )}

      {/* New Document Modal */}
      <Dialog open={newDocumentModal} onOpenChange={setNewDocumentModal}>
        <DialogContent className="sm:max-w-[95vw] w-[95vw] h-[90vh] flex flex-col p-0 gap-0" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader className="py-4 border-b flex-shrink-0">
            <DialogTitle className="px-6">Create New {currentSelectedType}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
             <FormInput 
                formTemplate={currentSelectedType ? JSON.parse(getFormTemplate(currentSelectedType)) : []} 
                formState={newDocumentState} 
                setFormState={setNewDocumentState} 
                onSubmit={handleCreateDocument}
             />
          </div>

          <div className="py-4 border-t bg-gray-50 flex justify-end gap-2 flex-shrink-0">
            <Button onClick={handleCreateDocument}>Save Document</Button>
            <Button variant="outline" onClick={() => setNewDocumentModal(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
